import {
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";
import PageTitle from "components/PageTitle/PageTitle";
import { useEffect, useState } from "react";
import { dbGet, dbPut } from "utils/DBFetchers";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import PopupMessage from "components/PopupMessage/PopupMessage";
import PharmacySelector from "components/CustomFields/PharmacySelector";
import useStyles from "./styles";
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import ScienceIcon from '@mui/icons-material/Science';

export default function PharmacyWalletActivationPage() {
  const classes = useStyles();
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();

  const [showConfirmationPopUp, setShowConfirmationPopUp] = useState(false);
  const [showTestTogglePopup, setShowTestTogglePopup] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const [selectedRut, setSelectedRut] = useState("");
  const [walletStatus, setWalletStatus] = useState("");
  const [currentWalletStatus, setCurrentWalletStatus] = useState("");

  const [isTestPharmacy, setIsTestPharmacy] = useState(false);

  useEffect(() => {
    if (!selectedRut) {
      setWalletStatus("");
      setCurrentWalletStatus("");
      setIsTestPharmacy(false);
      return;
    }

    const fetchStatus = async () => {
      setStatusLoading(true);
      try {
        const data = await dbGet(`wallet/internal/status/pharmacies/${selectedRut}`);
        let currentStatus = data.wallet_status || "";
        if (currentStatus === "activated-api") {
          currentStatus = "activated-api-lioren";
        }
        setWalletStatus(currentStatus);
        setCurrentWalletStatus(currentStatus);
        setIsTestPharmacy(Boolean(data.is_test_pharmacy));
      } catch (error) {
        snackbarOpen("error", "No se pudo obtener el estado actual de la billetera.");
        setWalletStatus("");
        setCurrentWalletStatus("");
        setIsTestPharmacy(false);
      } finally {
        setStatusLoading(false);
      }
    };

    fetchStatus();
  }, [selectedRut]);

  const onChangeSelectedRut = (rut) => {
    setSelectedRut(rut);
  };

  const onConfirm = () => {
    setShowConfirmationPopUp(false);
  };

  const onReconfirm = () => {
    if (submitting) return;
    setSubmitting(true);

    let statusForBack = walletStatus;
    if (walletStatus === "activated-api-lioren" || walletStatus === "activated-api-golan") {
      statusForBack = "activated-api";
    }

    const payload = {
      status: statusForBack,
      is_test_pharmacy: isTestPharmacy,
    };

    dbPut(`wallet/internal/status/pharmacies/${selectedRut}`, payload)
      .then(() => {
        snackbarOpen("success", "Estado de la billetera cambiado con éxito");
        setCurrentWalletStatus(walletStatus);
      })
      .catch((error) => {
        const error_key = error?.errors?.key?.[0] || "";
        snackbarOpen("error", error_key || "Error al cambiar el estado de la billetera");
      })
      .finally(() => {
        setSubmitting(false);
        setShowConfirmationPopUp(false);
      });
  };

  const handleToggleTestPharmacy = async () => {
    if (submitting || !selectedRut) return;

    try {
      setSubmitting(true);

      // ✅ Nuevo endpoint dedicado
      await dbPut(`wallet/internal/pharmacies/is_testing_pharmacy/${selectedRut}`, {
        is_testing_pharmacy: !isTestPharmacy,
      });

      setIsTestPharmacy(!isTestPharmacy);

      snackbarOpen(
        "success",
        !isTestPharmacy
          ? "Marcada como farmacia de prueba."
          : "Marcada como farmacia real."
      );
    } catch (error) {
      const msg =
        error?.errors?.key?.[0] ||
        "No se pudo cambiar el tipo de farmacia.";
      snackbarOpen("error", msg);
    } finally {
      setSubmitting(false);
      setShowTestTogglePopup(false);
    }
  };
  const statusText =
    walletStatus === "activated-api-lioren"
      ? "Activada por API (Lioren)"
      : walletStatus === "activated-api-golan"
        ? "Activada por API (Golan)"
          : walletStatus === "activated-ui"
            ? "Activada por Carga Interactiva"
            : walletStatus === "deactivated"
              ? "Desactivada"
              : "-";
  return (
    <Grid>
      {/* Popup para toggle de prueba */}
      <PopupMessage
        title={"¿Cambiar tipo de farmacia?"}
        message={
          isTestPharmacy
            ? "Las farmacias asociadas al rut " + selectedRut + " dejarán de ser consideradas de prueba y se incluirán en los reportes."
            : "Las farmacias asociadas al rut " + selectedRut + " serán consideradas como farmacias de prueba y no se incluirán en los reportes."
        }
        open={showTestTogglePopup}
        handleClose={() => setShowTestTogglePopup(false)}
        executeButtonRight={
          <Button
            onClick={() => handleToggleTestPharmacy()}
            variant="contained"
            color="secondary"
            disabled={submitting}
          >
            Confirmar
          </Button>
        }
      />


      <PopupMessage
        title={"¿Confirma la operación?"}
        message={
          selectedRut ? (
            <>
              Se cambiará el estado de billetera de las farmacias con RUT <b>{selectedRut}</b> por:{" "}
              <b>
                <i>
                  <span style={{ color: "#c62828" }}>{statusText}</span>
                </i>
              </b>
              .
              <br />
              <br />
              <span role="img" aria-label="warning">⚠️</span>{" "}
              <b>Importante:</b> Este cambio afectará las operaciones futuras de las farmacias asociadas al RUT.
            </>
          ) : (
            "Debe seleccionar una farmacia antes de continuar."
          )
        }
        open={showConfirmationPopUp}
        handleClose={onConfirm}
        executeButtonRight={
          <Button
            onClick={onReconfirm}
            variant="contained"
            color="secondary"
            className={classes.buttonSpacing}
            disabled={submitting || !selectedRut || !walletStatus}
            aria-busy={submitting}
            style={{ minWidth: 140 }}
          >
            {submitting ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CircularProgress size={18} color="inherit" />
                Procesando...
              </span>
            ) : (
              "Confirmar"
            )}
          </Button>
        }
        contentStyle={{ maxWidth: 500 }} // no hace falta whiteSpace: "pre-line"
      />

      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />

      <PageTitle title="Billetera virtual - Activar/Desactivar" />

      <Paper elevation={3} className={classes.paperContainer}>
        <Grid
          container
          direction="column"
          spacing={3}
          style={{ padding: 16, background: "#f5f5f5" }}
        >
          <Grid item>
            <Grid
              container
              alignItems="flex-end"
              justifyContent="space-between"
              spacing={2}
            >
              {/* Columna izquierda: farmacia */}
              <Grid item xs={12} sm={6}>
                <div style={{ maxWidth: 560, position: "relative" }}>
                  <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                    RUT de farmacia
                  </Typography>

                  <PharmacySelector
                    fieldClassName={classes.pharmacySelectionField}
                    value={selectedRut}
                    onChange={onChangeSelectedRut}
                    program={1}
                    only_with_wallet={false}
                    group_by_rut={true}
                  />

                  {/* Línea sutil “flotando” (no afecta layout) */}
                  {selectedRut && (
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 80 + 6,
                        pointerEvents: "auto",
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{
                          color: "#777",
                          fontSize: 13,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {isTestPharmacy ? <><ScienceIcon
                          fontSize="small"
                          style={{ color: "#757575", marginLeft: 6, verticalAlign: "text-bottom" }}
                          aria-label="Farmacia de prueba"
                        />Farmacia de prueba</> : <>Farmacia real</>}
                        <Button
                          size="small"
                          color="primary"
                          style={{
                            textTransform: "none",
                            fontSize: 13,
                            padding: 0,
                            minWidth: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                          }}
                          onClick={() => setShowTestTogglePopup(true)}
                          disabled={statusLoading || submitting}
                          aria-label="Editar tipo de farmacia"
                        >
                          Cambiar
                          <ChangeCircleIcon style={{ fontSize: 22 }} />
                        </Button>
                      </Typography>
                    </div>
                  )}
                </div>
              </Grid>



              {/* Columna derecha: Estado + botón */}
              <Grid
                item
                xs={12}
                sm={6}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 16,
                  maxWidth: 560,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography style={{ fontWeight: 700 }}>
                    Estado de billetera
                  </Typography>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    disabled={!selectedRut}
                    style={{ height: 56 }}
                  >
                    <Select
                      value={walletStatus}
                      onChange={(e) => setWalletStatus(e.target.value)}
                      displayEmpty
                      style={{
                        height: "56px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <MenuItem value="" disabled>
                        Seleccione un estado
                      </MenuItem>
                      <MenuItem value="activated-api-lioren">
                        Activada por API (Lioren)
                      </MenuItem>
                      <MenuItem value="activated-api-golan">
                        Activada por API (Golan)
                      </MenuItem>
                      <MenuItem value="activated-ui">
                        Activada por Carga Interactiva
                      </MenuItem>
                      <MenuItem value="deactivated">Desactivada</MenuItem>
                    </Select>
                  </FormControl>
                </div>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setShowConfirmationPopUp(true)}
                  disabled={
                    statusLoading ||
                    submitting ||
                    !walletStatus ||
                    !selectedRut ||
                    walletStatus === currentWalletStatus
                  }
                  style={{
                    height: 56,
                    minWidth: 160,
                    whiteSpace: "nowrap",
                  }}
                >
                  {walletStatus === currentWalletStatus ? "Estado actual" : "Cambiar estado"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}
